import React from "react";
import styled, { keyframes } from "styled-components";
import { HomeButton } from "./MemoryPalAILearnMore";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MethodologyContainer = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 6rem 2rem;
  font-family: "VT323", monospace !important;
  color: #fff;
  background-color: transparent;

  & * {
    font-family: "VT323", monospace !important;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: #43fe47;
  text-transform: uppercase;
  margin-bottom: 4rem;
  text-align: center;
  letter-spacing: 2px;
  border-bottom: 1px solid rgba(67, 254, 71, 0.3);
  display: inline-block;
  width: 100%;
`;

const StepWrapper = styled.div`
  position: relative;
  padding-left: 50px;
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: ${(props) => props.delay}s;
  opacity: 0;

  /* The vertical line connecting steps */
  &:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 15px;
    top: 40px;
    bottom: -4rem;
    width: 1px;
    background: linear-gradient(to bottom, #43fe47, transparent);
  }
`;

const StepIcon = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 30px;
  height: 30px;
  background: #000;
  border: 2px solid #43fe47;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #43fe47;
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 0 10px rgba(67, 254, 71, 0.5);
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 1rem;
`;

const StepNumber = styled.span`
  color: #43fe47;
  font-size: 1.5rem;
  font-weight: bold;
  opacity: 0.7;
`;

const StepTitle = styled.h2`
  font-size: 2rem;
  color: #43fe47;
  text-transform: uppercase;
  margin: 0;
  letter-spacing: 1px;
`;

const StepDescription = styled.p`
  font-size: 1.3rem;
  line-height: 1.5;
  color: #e0e0e0;
  margin: 0;
  max-width: 800px;
  text-align: justify;
`;

const MetaTag = styled.span`
  font-size: 0.9rem;
  background: rgba(67, 254, 71, 0.1);
  border: 1px solid #43fe47;
  padding: 2px 8px;
  margin-top: 15px;
  display: inline-block;
  color: #43fe47;
  text-transform: uppercase;
`;

const Methodology = () => {
  const steps = [
    {
      id: "I",
      title: "Decode",
      content: "Our journey begins with deep discovery — an exploration of your goals, audience, and creative challenges. We look beyond data to understand the human story behind your vision, uncovering where technology, psychology, and design can create the greatest impact. This phase builds the foundation for meaningful, purpose-driven collaboration."
    },
    {
      id: "II",
      title: "Design the Framework",
      content: "Armed with insight, we design a strategy that aligns intelligence with intention. Our frameworks connect brand identity, user experience, and system design into a cohesive roadmap. Every step is crafted to amplify what makes your work distinct — balancing structure with creative freedom."
    },
    {
      id: "III",
      title: "Build the Experience",
      content: "With strategy in place, we move into creation — where ideas become interactive realities. Whether developing AI-powered tools, symbolic brand systems, or adaptive learning experiences, we merge storytelling, accessibility, and innovation to build solutions that feel human at their core."
    },
    {
      id: "IV",
      title: "Evolve Through Iteration",
      content: "Creation is never static. Through continuous collaboration, testing, and refinement, we evolve each project in real time. Feedback becomes a creative feedback loop, ensuring every iteration moves closer to resonance — technically sound, emotionally intelligent, and true to your mission."
    },
    {
      id: "V",
      title: "Launch the Living System",
      content: "Once refined, we launch your solution with confidence. Every component is tested for performance, accessibility, and scalability. The result is not a finished product but a living system — built to grow, adapt, and connect meaningfully with its users over time."
    },
    {
      id: "VI",
      title: "Sustain and Expand",
      content: "Our partnership extends beyond launch. As your platform expands and technologies evolve, we stay engaged — monitoring performance, optimizing experiences, and introducing new innovations. StudioVoice2Fly is built on long-term evolution, helping your ecosystem stay aligned, adaptive, and alive."
    }
  ];

    return (
      <>
        <HomeButton to="/">Home</HomeButton>
        <MethodologyContainer>
        <PageTitle>System Methodology</PageTitle>

        {steps.map((step, index) => (
            <StepWrapper key={step.id} delay={index * 0.2}>
            <StepIcon>{index + 1}</StepIcon>
            <StepHeader>
                <StepNumber>{step.id}.</StepNumber>
                <StepTitle>{step.title}</StepTitle>
            </StepHeader>
            <StepDescription>{step.content}</StepDescription>
            <MetaTag>Phase_{step.title.replace(/\s+/g, '_').toLowerCase()}</MetaTag>
            </StepWrapper>
        ))}
        </MethodologyContainer>
      </>

  );
};

export default Methodology;